import { motion, useReducedMotion } from "framer-motion";
import { Children, isValidElement, type ReactNode } from "react";

/**
 * Word-by-word stagger reveal for a heading. Splits text on whitespace,
 * wraps each word in `overflow-hidden` and slides the inner span up from
 * below. Plays once on scroll-into-view. Respects prefers-reduced-motion.
 *
 * Supports plain string children and a single-level mix of strings + spans
 * (so you can still emphasise individual words: pass `<span>italic</span>`
 * inline). Anything more complex falls back to no animation.
 */
export function WordReveal({
  children,
  className,
  delay = 0,
  stagger = 0.02,
  as: As = "span",
  id,
}: {
  children: ReactNode;
  className?: string;
  delay?: number;
  stagger?: number;
  as?: "h1" | "h2" | "h3" | "p" | "span";
  id?: string;
}) {
  const reduced = useReducedMotion();
  const tokens = collectTokens(children);

  if (reduced || tokens.length === 0) {
    return (
      <As id={id} className={className}>
        {children}
      </As>
    );
  }

  return (
    <As id={id} className={className}>
      <span className="sr-only">{tokens.map((t) => t.text).join(" ")}</span>
      <span aria-hidden>
        {tokens.map((token, i) => (
          <span key={i}>
            <span className="inline-block overflow-hidden pb-[0.14em] align-bottom leading-[1.16]">
              <motion.span
                className={`inline-block ${token.italic ? "italic" : ""}`}
                initial={{ y: "55%", opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true, margin: "-10%" }}
                transition={{
                  duration: 0.42,
                  delay: delay + i * stagger,
                  ease: [0.22, 1, 0.36, 1],
                }}
              >
                {token.text}
              </motion.span>
            </span>
            {i < tokens.length - 1 ? " " : null}
          </span>
        ))}
      </span>
    </As>
  );
}

type Token = { text: string; italic: boolean };

function collectTokens(node: ReactNode): Token[] {
  const out: Token[] = [];
  Children.forEach(node, (child) => {
    if (typeof child === "string" || typeof child === "number") {
      for (const word of String(child).split(/\s+/).filter(Boolean)) {
        out.push({ text: word, italic: false });
      }
      return;
    }
    if (isValidElement(child)) {
      // Allow a single-level <span> or <em> with string children.
      const inner = (child.props as { children?: ReactNode }).children;
      if (typeof inner === "string") {
        const italic =
          child.type === "em" ||
          (typeof child.type === "string" &&
            (child.props as { className?: string }).className?.includes("italic") === true);
        for (const word of inner.split(/\s+/).filter(Boolean)) {
          out.push({ text: word, italic });
        }
      }
    }
  });
  return out;
}
